import { jsPDF } from 'jspdf';
import { isEmbeddedFontLoaded, registerEmbeddedFont } from '../pdf/embeddedFont.js';

/**
 * Nachweis der Unterweisung als PDF (A4 hoch).
 *
 * Reine Funktion: Eingabe sind die eingefrorenen Angaben der Teilnahme
 * (§2 des Bauplans — das Zertifikat sagt, worauf unterschrieben wurde),
 * Ausgabe sind die Bytes. Sie laeuft in Node (api/unterweisung-zertifikat.ts)
 * UND im Browser (Vorschau/erneut senden im Innendienst), deshalb kein
 * `window`, kein `document`, kein `doc.save()`.
 *
 * Schrift: die eingebettete Liberation Sans (pdf/embeddedFont.ts) traegt die
 * Umlaute sicher. Sie wird nur benutzt, wenn der Aufrufer sie vorher mit
 * `ensureEmbeddedFont()` geladen hat — diese Funktion bleibt synchron und
 * laedt nichts nach. Ohne geladene Schrift entsteht das Blatt mit der
 * Standard-Helvetica; jsPDF kann dort die deutschen Umlaute (WinAnsi)
 * darstellen, nur ist das PDF dann nicht PDF/A-tauglich.
 *
 * Datumsangaben kommen als ISO-Zeichenketten und werden per Zeichenkette
 * nach de-DE umgestellt — KEIN `new Date('YYYY-MM-DD')`, das in Node als UTC
 * und im Browser je nach Zone einen Tag zu frueh liegt.
 */
export interface ZertifikatDaten {
    /** Betreuendes Buero (Briefkopf), z. B. „GefAS mbH". */
    bueroName: string;
    /** Anschrift des Bueros, Zeilen durch \n oder Komma getrennt. */
    bueroAdresse?: string | null;
    /** Logo als DataURL (PNG/JPEG); anderes wird stillschweigend uebersprungen. */
    bueroLogoDataUrl?: string | null;
    /** Kunde = Projekt (ein SiFa-Kunde ist ein Dauerprojekt). */
    kundeName: string;
    /** Titel der Unterweisung (Kurs). */
    titel: string;
    vorname: string;
    nachname: string;
    /** ISO-Datum YYYY-MM-DD. */
    geburtsdatum?: string | null;
    /** ISO-Zeitstempel oder ISO-Datum des Abschlusses. */
    abgeschlossenAm: string;
    /** ISO-Datum; null/leer = ohne Ablauf. */
    gueltigBis?: string | null;
    zertifikatNr: string;
    punkte: number;
    punkteMax: number;
    /** PNG-DataURL der Unterschrift (SignaturePad). */
    unterschriftDataUrl?: string | null;
    /** QR-Code als DataURL (vom Aufrufer erzeugt, `qrcode`.toDataURL). */
    qrDataUrl?: string | null;
}

/** „2026-09-07" bzw. „2026-09-07T13:05:00Z" → „07.09.2026"; Unbekanntes bleibt wie es ist. */
export const datumDe = (iso: string | null | undefined): string => {
    const s = (iso || '').trim();
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    if (!m) return s;
    return `${m[3]}.${m[2]}.${m[1]}`;
};

/** Bildformat aus der DataURL fuer jsPDF.addImage; undefined = nicht einbettbar. */
const bildFormat = (dataUrl: string | null | undefined): 'PNG' | 'JPEG' | undefined => {
    const s = (dataUrl || '').trim();
    if (/^data:image\/png;base64,/i.test(s)) return 'PNG';
    if (/^data:image\/jpe?g;base64,/i.test(s)) return 'JPEG';
    return undefined;
};

/** Anschriftzeilen wie in composeInvoiceFooter: Umbruch UND Komma trennen. */
const adressZeilen = (adresse: string | null | undefined): string[] =>
    (adresse || '')
        .split(/\r?\n|,/)
        .map((t) => t.trim())
        .filter(Boolean);

const SEITE_B = 210;
const SEITE_H = 297;
const RAND = 22;
const BREITE = SEITE_B - 2 * RAND;

/**
 * Erzeugt das Zertifikat und liefert die Bytes.
 *
 * Nichts an der Eingabe wird umgedeutet: leerer Name bleibt leer, fehlende
 * Unterschrift laesst die Linie blank — der Aufrufer entscheidet vorher, ob
 * die Teilnahme ueberhaupt ein Zertifikat verdient (bestanden, abgeschlossen).
 */
export const erzeugeZertifikatPdf = (daten: ZertifikatDaten): ArrayBuffer => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    if (isEmbeddedFontLoaded()) registerEmbeddedFont(doc);

    const text = (s: string) => (s ?? '').toString();
    let y = RAND;

    // ── Kopf: Buero links, Logo rechts ────────────────────────────────────
    const logoFormat = bildFormat(daten.bueroLogoDataUrl);
    if (logoFormat) {
        try {
            const props = doc.getImageProperties(daten.bueroLogoDataUrl as string);
            const maxB = 45;
            const maxH = 18;
            const faktor = Math.min(maxB / props.width, maxH / props.height);
            const b = props.width * faktor;
            const h = props.height * faktor;
            doc.addImage(daten.bueroLogoDataUrl as string, logoFormat, SEITE_B - RAND - b, y - 4, b, h);
        } catch {
            // Ein unlesbares Logo darf den Nachweis nicht verhindern.
        }
    }

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(30, 41, 59);
    doc.text(text(daten.bueroName), RAND, y);
    y += 5;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor(100, 116, 139);
    for (const zeile of adressZeilen(daten.bueroAdresse)) {
        doc.text(zeile, RAND, y);
        y += 4;
    }

    // Trennlinie unter dem Kopf
    y = Math.max(y, RAND + 20);
    doc.setDrawColor(13, 148, 136);
    doc.setLineWidth(0.8);
    doc.line(RAND, y, SEITE_B - RAND, y);
    y += 22;

    // ── Titel ─────────────────────────────────────────────────────────────
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(26);
    doc.setTextColor(15, 23, 42);
    doc.text('Nachweis der Unterweisung', SEITE_B / 2, y, { align: 'center' });
    y += 12;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(14);
    doc.setTextColor(13, 148, 136);
    const titelZeilen = doc.splitTextToSize(text(daten.titel), BREITE) as string[];
    doc.text(titelZeilen, SEITE_B / 2, y, { align: 'center' });
    y += titelZeilen.length * 6.5 + 10;

    // ── Teilnehmer ────────────────────────────────────────────────────────
    doc.setFontSize(11);
    doc.setTextColor(71, 85, 105);
    doc.text('Hiermit wird bestätigt, dass', SEITE_B / 2, y, { align: 'center' });
    y += 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(15, 23, 42);
    const name = [text(daten.vorname), text(daten.nachname)].map((s) => s.trim()).filter(Boolean).join(' ');
    doc.text(name || '—', SEITE_B / 2, y, { align: 'center' });
    y += 7;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    if (daten.geburtsdatum) {
        doc.text(`geboren am ${datumDe(daten.geburtsdatum)}`, SEITE_B / 2, y, { align: 'center' });
        y += 6;
    }
    y += 6;

    doc.setFontSize(11);
    doc.setTextColor(71, 85, 105);
    const satz = doc.splitTextToSize(
        `die oben genannte Unterweisung für ${text(daten.kundeName) || 'den Auftraggeber'} vollständig durchlaufen und die Lernerfolgskontrolle bestanden hat.`,
        BREITE - 20,
    ) as string[];
    doc.text(satz, SEITE_B / 2, y, { align: 'center' });
    y += satz.length * 5.5 + 12;

    // ── Angaben in zwei Spalten ───────────────────────────────────────────
    const zeilen: Array<[string, string]> = [
        ['Kunde', text(daten.kundeName)],
        ['Datum', datumDe(daten.abgeschlossenAm)],
        ['Gültig bis', daten.gueltigBis ? datumDe(daten.gueltigBis) : 'ohne Ablauf'],
        ['Ergebnis', `bestanden, ${daten.punkte} von ${daten.punkteMax} Punkten`],
        ['Zertifikat-Nr.', text(daten.zertifikatNr)],
    ];
    const spalteX = RAND + 6;
    const wertX = RAND + 46;
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.setFontSize(10.5);
    for (const [label, wert] of zeilen) {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(100, 116, 139);
        doc.text(label, spalteX, y);
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(15, 23, 42);
        const wertZeilen = doc.splitTextToSize(wert || '—', SEITE_B - RAND - wertX - 6) as string[];
        doc.text(wertZeilen, wertX, y);
        y += wertZeilen.length * 5.5 + 2;
        doc.line(spalteX, y - 1.5, SEITE_B - RAND - 6, y - 1.5);
        y += 4;
    }

    // ── Unterschrift (links unten) + QR (rechts unten) ────────────────────
    const fussY = SEITE_H - RAND - 6;
    const unterschriftY = fussY - 30;
    const unterschriftB = 70;
    const unterschriftFormat = bildFormat(daten.unterschriftDataUrl);
    if (unterschriftFormat) {
        try {
            const props = doc.getImageProperties(daten.unterschriftDataUrl as string);
            const faktor = Math.min(unterschriftB / props.width, 24 / props.height);
            const b = props.width * faktor;
            const h = props.height * faktor;
            doc.addImage(daten.unterschriftDataUrl as string, unterschriftFormat, RAND, unterschriftY - h - 1, b, h);
        } catch {
            // Ohne Bild bleibt die Linie leer — besser als kein Nachweis.
        }
    }
    doc.setDrawColor(71, 85, 105);
    doc.setLineWidth(0.4);
    doc.line(RAND, unterschriftY, RAND + unterschriftB, unterschriftY);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(100, 116, 139);
    doc.text('Unterschrift Teilnehmer/in', RAND, unterschriftY + 4.5);

    const qrFormat = bildFormat(daten.qrDataUrl);
    if (qrFormat) {
        const qrGroesse = 28;
        try {
            doc.addImage(daten.qrDataUrl as string, qrFormat, SEITE_B - RAND - qrGroesse, unterschriftY - qrGroesse + 2, qrGroesse, qrGroesse);
            doc.setFontSize(7.5);
            doc.text(text(daten.zertifikatNr), SEITE_B - RAND - qrGroesse / 2, unterschriftY + 4.5, { align: 'center' });
        } catch {
            // Ein kaputter QR-Code ist kein Grund, das Zertifikat zu verweigern.
        }
    }

    // ── Fusszeile ─────────────────────────────────────────────────────────
    doc.setDrawColor(226, 232, 240);
    doc.setLineWidth(0.3);
    doc.line(RAND, fussY - 4, SEITE_B - RAND, fussY - 4);
    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text(`Erstellt mit OS:IA · ${text(daten.bueroName)}`, SEITE_B / 2, fussY, { align: 'center' });

    return doc.output('arraybuffer');
};
